import React, {useState, useRef, useEffect, useMemo, useCallback} from 'react';
import Box from '@mui/material/Box';
import AurIcon, {IconNames} from '../common/aur-icon/AurIcon';
import AurTabWithIcons, {
  ITabIconLabel,
} from '../common/aur-tab-with-icons/AurTabWithIcons';
import {t} from 'i18next';
import {Divider, Drawer, Grid, Typography} from '@mui/material';
import {
  ECA_DRAWER_ACTIVE_TAB,
  LOAD_DOC_TYPE,
  LOAD_TYPE,
} from '../../constants/constants';
import AurButton from '../common/aur-button/AurButton';
import AurIconButton from '../common/aur-button/AurIconButton';
import LoadsTab from '../ecapages/eca-overview/LoadsTab';
import {LoadFileData} from '../client-details/project-data-table/types';
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../../redux/store';
import {getEcaSummary} from '../../api/EcaManagementService';
import ECAFlyout from './ECAFlyout';
import {extractSavedSearchId, formateNumber} from '../../utils/utils';
import SavedSearchPanel from './SavedSearchPanel';
import ECATagTab from './ECATagTab';
import {
  IGlobalSelectedItem,
  setAllDocumentsData,
  setCloseFlyout,
  setIsDeduplicationModal,
  setSavedSearchDeleteConfirm,
} from '../../redux/slices/ecaAdhocSearchSlice';
import {ExtendedTreeItemProps} from '../eca-doc-list/eca-save-search/EcaSaveSearch';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {setNewSearch} from '../../redux/slices/ecaDocListSlice';
import AurPopup from '../common/aur-popup/AurPopup';
import {deleteSavedSearch} from '../../api/EcaServices';
import AurToast from '../common/aur-toast/AurToast';
import {
  setHighLightedWords,
  setShowHighLightedTab,
} from '../../redux/slices/ecaDocumentViewerSlice';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../hooks/permissions/usePermissionsByPage';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {AxiosError} from 'axios';
import {showErrorToast} from '../../api/utils';

interface ECADrawerWithTabsProps {
  showDrawer: boolean;
  handleDrawer: (value: boolean) => void;
  selectedIndex: string;
  handleViewDocs: (flag: string, selectedItem: IGlobalSelectedItem) => void;
  activeTab: number;
  setActiveTab: (data: number) => void;
  activeLoadFile: LoadFileData | null;
  setActiveLoadFile: (data: LoadFileData | null) => void;
  selectedTag?: TreeViewBaseItem<ExtendedTreeItemProps> | null;
  selectedNode: TreeViewBaseItem<ExtendedTreeItemProps> | null;
  expandedIds?: string[];
  loadWithFlyout: boolean;
  handleUseAsScope?: (flag: string, selectedItem: IGlobalSelectedItem) => void;
  handleAddToTemplate?: (selectedItem: IGlobalSelectedItem) => void;
  onPickTreeItem?: (
    treeItem: TreeViewBaseItem<ExtendedTreeItemProps> | null
  ) => void;
}

interface SavedSearchType {
  id: string;
  label: string;
}

const headerHeight = 72;
const listItemWidth = 128;

type EcaTreeData = TreeViewBaseItem<ExtendedTreeItemProps>[];

interface EcaTreeCacheEntry {
  savedSearchRoot?: EcaTreeData;
  tagTree?: EcaTreeData;
}

const normalizeCachePart = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const getEcaTreeCacheKey = (
  clientId: unknown,
  projectId: unknown
): string | undefined => {
  const normalizedClientId = normalizeCachePart(clientId);
  const normalizedProjectId = normalizeCachePart(projectId);

  if (!normalizedClientId || !normalizedProjectId) {
    return undefined;
  }

  return JSON.stringify([normalizedClientId, normalizedProjectId]);
};

const ECADrawerWithTabs: React.FC<ECADrawerWithTabsProps> = ({
  showDrawer,
  handleDrawer,
  selectedIndex,
  activeTab,
  setActiveTab,
  activeLoadFile,
  setActiveLoadFile,
  selectedTag = null,
  selectedNode = null,
  expandedIds = [''],
  handleViewDocs,
  loadWithFlyout = false,
  handleUseAsScope,
  handleAddToTemplate,
  onPickTreeItem,
}) => {
  const [drawerWidth, setDrawerWidth] = useState<number>(300);
  const [ecaSummaryData, setEcaSummaryData] = useState<LoadFileData | null>(
    null
  );
  const [showFlyout, setShowFlyout] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // position the anchor element in line with the selected element
  const [positions, setPositions] = useState({
    top: 0,
    left: 0,
  });

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const {projectId} = useSelector((state: RootState) => state.reports);

  const {ecaDrawerFlag} = useSelector(
    (state: RootState) => state.ecaAdhocSearch
  );
  const {taggingStatus} = useSelector(
    (state: RootState) => state.ecaDocList
  );

  const treeCacheRef = useRef<Map<string, EcaTreeCacheEntry>>(new Map());
  const activeCacheKey = useMemo(
    () => getEcaTreeCacheKey(clientId, projectId),
    [clientId, projectId]
  );
  const activeCacheKeyRef = useRef(activeCacheKey);
  activeCacheKeyRef.current = activeCacheKey;
  const [cacheRevision, setCacheRevision] = useState(0);
  const activeCacheEntry = useMemo(
    () =>
      activeCacheKey
        ? treeCacheRef.current.get(activeCacheKey)
        : undefined,
    [activeCacheKey, cacheRevision]
  );

  const updateSavedSearchCache = useCallback(
    (data?: EcaTreeData) => {
      if (
        !activeCacheKey ||
        activeCacheKeyRef.current !== activeCacheKey
      ) {
        return;
      }

      const previousEntry =
        treeCacheRef.current.get(activeCacheKey) ?? {};

      if (data === undefined) {
        if (previousEntry.savedSearchRoot === undefined) {
          return;
        }

        const remainingEntry = {...previousEntry};
        delete remainingEntry.savedSearchRoot;
        treeCacheRef.current.set(activeCacheKey, remainingEntry);
      } else {
        treeCacheRef.current.set(activeCacheKey, {
          ...previousEntry,
          savedSearchRoot: data,
        });
      }

      setCacheRevision((revision) => revision + 1);
    },
    [activeCacheKey]
  );

  const updateTagCache = useCallback(
    (data?: EcaTreeData) => {
      if (
        !activeCacheKey ||
        activeCacheKeyRef.current !== activeCacheKey
      ) {
        return;
      }

      const previousEntry =
        treeCacheRef.current.get(activeCacheKey) ?? {};

      if (data === undefined) {
        if (previousEntry.tagTree === undefined) {
          return;
        }

        const remainingEntry = {...previousEntry};
        delete remainingEntry.tagTree;
        treeCacheRef.current.set(activeCacheKey, remainingEntry);
      } else {
        treeCacheRef.current.set(activeCacheKey, {
          ...previousEntry,
          tagTree: data,
        });
      }

      setCacheRevision((revision) => revision + 1);
    },
    [activeCacheKey]
  );

  const previousEcaDrawerFlag = useRef(ecaDrawerFlag);
  const previousTaggingStatus = useRef(taggingStatus);

  useEffect(() => {
    const isRefreshSignal =
      ecaDrawerFlag && !previousEcaDrawerFlag.current;
    previousEcaDrawerFlag.current = ecaDrawerFlag;

    if (
      isRefreshSignal &&
      activeTab !== ECA_DRAWER_ACTIVE_TAB.SAVE_SEARCHES
    ) {
      updateSavedSearchCache();
    }
  }, [activeTab, ecaDrawerFlag, updateSavedSearchCache]);

  useEffect(() => {
    const isSuccessfulTagMutation =
      taggingStatus === 'Completed' &&
      previousTaggingStatus.current !== 'Completed';
    previousTaggingStatus.current = taggingStatus;

    if (
      isSuccessfulTagMutation &&
      activeTab !== ECA_DRAWER_ACTIVE_TAB.TAGS
    ) {
      updateTagCache();
    }
  }, [activeTab, taggingStatus, updateTagCache]);

  const getUserId: string | undefined = useSelector(
    (state: RootState) => state.user.user
  )?.userId;

  const {hasSavedSearchesPerm} = usePermissionsByPage(
    PAGE_NAME.INDEX_SEARCH_TAB
  );

  const drawerRef = useRef<HTMLDivElement>(null);

  const [selectedSavedSearch, setSelectedSavedSearch] =
    useState<SavedSearchType | null>(null);

  // Use ResizeObserver to dynamically update drawer width
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (drawerRef.current) {
        setDrawerWidth(drawerRef.current.offsetWidth);
      }
    });

    // Observe the drawer's ref for size changes
    if (drawerRef.current) {
      resizeObserver.observe(drawerRef.current);
    }

    // Cleanup the observer when component unmounts or drawerRef changes
    return () => {
      if (drawerRef.current) {
        resizeObserver.unobserve(drawerRef.current);
      }
    };
  }, [showDrawer]); // Re-run when showDrawer changes

  useEffect(() => {
    if (activeTab === ECA_DRAWER_ACTIVE_TAB.LOADS) {
      fetchEcaSummary();
    }
  }, [activeTab]);

  const dispatch = useDispatch();
  const closeFlyout = useSelector(
    (state: RootState) => state.ecaAdhocSearch.closeFlyout
  );
  useEffect(() => {
    handleClose();
    dispatch(setCloseFlyout(false));
  }, [closeFlyout]);

  const ecaAdmin = useSelector(
    (state: RootState) => state.ecaAdhocSearch.checkEcaAdmin
  );

  const fetchEcaSummary = async () => {
    try {
      const paramString = `clientId=${clientId}&projectid=${projectId}`;
      const response = await getEcaSummary(paramString);
      const res = await response.data;
      const data = res?.data;
      setEcaSummaryData(data);
      dispatch(setAllDocumentsData({...data, type: LOAD_TYPE.ALL_DOCUMENT}));
    } catch (error) {
      console.log(error);
    }
  };

  const tabIconLabelArray: ITabIconLabel[] = [
    {
      tabIndex: 0,
      label: t('indexSearchPage.aurTabControl.loads'),
      icon: IconNames.PostAddOutlined,
    },
    ...(hasSavedSearchesPerm
      ? [
          {
            tabIndex: 1,
            label: (
              <span>
                {t('indexSearchPage.aurTabControl.saved')}{' '}
                {t('indexSearchPage.aurTabControl.searches')}
              </span>
            ),
            icon: IconNames.BookmarksOutlined,
          },
        ]
      : []),
    {
      tabIndex: 2,
      label: t('indexSearchPage.aurTabControl.tags'),
      icon: IconNames.Tag,
    },
  ];

  const handleTabChange = (index: number) => {
    const tabIndex = tabIconLabelArray[index].tabIndex;
    setActiveTab(tabIndex);
    handleClose();
  };

  useEffect(() => {
    const element = document.getElementById('anchorElement');
    setAnchorEl(element);
  }, []);

  const handleSelectLoadFile = (
    loadFile: any,
    event?: React.MouseEvent<HTMLElement>
  ) => {
    setActiveLoadFile(loadFile);
    if (event) {
      const {top, right} = event.currentTarget.getBoundingClientRect();
      const offsetWidth = drawerRef?.current?.getBoundingClientRect();

      // Setting anchor element position aligned with the selected element
      setPositions({
        top,
        left: offsetWidth?.right ?? right,
      });
      setShowFlyout(true);
      setTimeout(() => {
        document.getElementById('eca-flyout-container')?.focus();
      }, 100);
    }
  };

  const handleClose = () => {
    setShowFlyout(false);
    setActiveLoadFile(null);
    setTimeout(() => {
      document.getElementById('eca-loads-tab')?.focus();
    }, 100);
  };

  const renderLoadFlyoutContent = () => {
    return (
      <div tabIndex={0} id="eca-flyout-container">
        <Box className="flyout-container">
          <Box className="flyout-header">
            <AurButton
              classNames={'close-button'}
              label={t('buttons.close')}
              type="exit"
              size="Small"
              title={t('buttons.close')}
              dataTestId="close-btn"
              onClick={handleClose}
              tabIndex={0}
            />
            <Typography className="typo-text headline2_size custom-title-text">
              {activeLoadFile?.type === LOAD_TYPE.ALL_DOCUMENT
                ? t('ecaHomepageModals.ecaBottom.allDocuments')
                : activeLoadFile?.name}
            </Typography>
            <Typography className="typo-text subhead4_size custom-color-white">
              <span>
                {activeLoadFile?.type === LOAD_TYPE.ALL_DOCUMENT
                  ? t('eca.flyout.docCount')
                  : t('eca.flyout.lastDocCount')}
              </span>
              <span className="typo-text body2_size custom-color">
                {formateNumber(activeLoadFile?.totalDocumentsCount ?? 0)}
              </span>
            </Typography>
          </Box>

          {activeLoadFile?.type === LOAD_TYPE.LOAD_DOCUMENT &&
            activeLoadFile?.evidenceSource && (
              <Box className="cell-container">
                <Grid container className="row-style">
                  <Grid item xs={4}>
                    <Typography className="typo-text body4_size custom-color-cell">
                      {t('eca.flyout.evidence')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography className="typo-text body4_size custom-color-cell">
                      {activeLoadFile?.evidenceSource}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

          <Box>
            <Box className="flyout-doc-button-container">
              <Box className="flyout-doc-text">
                <Typography className="typo-text body3_size custom-color">
                  {activeLoadFile?.type === LOAD_TYPE.ALL_DOCUMENT
                    ? t('eca.flyout.viewAllDocTitle')
                    : t('eca.flyout.viewDocTitle')}
                </Typography>
                <Typography className="typo-text label4_size custom-color-orange">
                  {t('eca.flyout.viewDocSubTitle')}
                </Typography>
              </Box>
              <AurButton
                classNames="doc-button"
                tabIndex={0}
                onClick={() => {
                  if (activeLoadFile?.type === LOAD_TYPE.ALL_DOCUMENT) {
                    dispatch(setNewSearch(true));
                    dispatch(setHighLightedWords([]));
                    dispatch(setShowHighLightedTab(false));
                    handleViewDocs(LOAD_DOC_TYPE.ALL_DOC, activeLoadFile);
                    handleClose();
                  } else {
                    handleViewDocs(LOAD_DOC_TYPE.LOAD_DOC, activeLoadFile);
                    dispatch(setHighLightedWords([]));
                    dispatch(setShowHighLightedTab(false));
                  }
                }}
                type="continue"
                size="Small"
                dataTestId="view-doc-button"
                label={t('eca.flyout.viewDocBtn')}
                minWidth={126}
              />
            </Box>
            {activeLoadFile?.type === LOAD_TYPE.LOAD_DOCUMENT && (
              <>
                <Divider orientation="horizontal" flexItem />
                <Box className="flyout-doc-button-container">
                  <Box className="flyout-doc-text">
                    <Typography className="typo-text body3_size custom-color">
                      {t('eca.flyout.scopeTitle')}
                    </Typography>
                  </Box>
                  <AurButton
                    tabIndex={0}
                    classNames="doc-button"
                    onClick={() => {
                      handleUseAsScope &&
                        handleUseAsScope(
                          LOAD_DOC_TYPE.LOAD_SCOPE,
                          activeLoadFile
                        );
                    }}
                    type="continue"
                    size="Small"
                    dataTestId="scope-button"
                    label={t('eca.flyout.scopeBtn')}
                    minWidth={126}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
      </div>
    );
  };

  const [noDeleteOption, setNoDeleteOption] = useState<boolean>(false);
  const [deleteSuccessToast, setDeleteSuccessToast] = useState<boolean>(false);
  const [savedSearchLabel, setSavedSearchLabel] = useState<string>('');
  const [isDeleteSuccessful, setIsDeleteSuccessful] = useState(false);

  const showConfirmDeletePopup = useSelector(
    (state: RootState) => state.ecaAdhocSearch.showSavedSearchConfirmDeletePopup
  );

  const {showToast} = useToast();

  const closeModalHandler = () => {
    setNoDeleteOption(false);
  };

  const handleContinue = () => {
    dispatch(setIsDeduplicationModal(false));
    setNoDeleteOption(false);
  };

  const closeConfirmDeleteModalHandler = () => {
    dispatch(setSavedSearchDeleteConfirm(false));
    dispatch(setIsDeduplicationModal(false));
  };

  const handleConfirmDeleteContinue = () => {
    dispatch(setIsDeduplicationModal(false));

    if (selectedSavedSearch) {
      deleteSavedSearchItem(selectedSavedSearch.id, selectedSavedSearch.label);
    }
  };

  const deleteSavedSearchItem = async (id: string, label: string) => {
    const params = `?clientId=${clientId}&projectId=${projectId}`;
    try {
      const results = await deleteSavedSearch(params, extractSavedSearchId(id));
      if (results.status == 200) {
        dispatch(setSavedSearchDeleteConfirm(false));
        setDeleteSuccessToast(true);
        setSavedSearchLabel(label);
        setTimeout(() => {
          setDeleteSuccessToast(false);
        }, 4000);

        setIsDeleteSuccessful(true);
        if (activeTab !== ECA_DRAWER_ACTIVE_TAB.SAVE_SEARCHES) {
          updateSavedSearchCache();
        }
        setTimeout(() => {
          setIsDeleteSuccessful(false);
        }, 5000);
      }
    } catch (e: any) {
      if (e.status == 409) {
        dispatch(setIsDeduplicationModal(true));
        setNoDeleteOption(true);
        dispatch(setSavedSearchDeleteConfirm(false));
      } else {
        // toast show when failed
        dispatch(setSavedSearchDeleteConfirm(false));
        const error = e as AxiosError;
        showErrorToast(showToast, error, 'center');
      }
    }
  };

  const handleSnackbarClose = () => {
    setDeleteSuccessToast(false);
  };

  const renderDrawerContent = () => {
    return (
      <Box className="drawer-content">
        <Box className="top-button-container">
          <AurIconButton
            size="big"
            icon={<AurIcon icon={IconNames.Close} />}
            onClick={() => {
              handleDrawer(false);
              handleClose();
            }}
            dataTestId="close-button"
            ariaLabel={t('buttons.close')}
          />
        </Box>
        <Divider orientation="horizontal" flexItem />
        <AurTabWithIcons
          defaultTabIndex={tabIconLabelArray.findIndex(
            (item) => item.tabIndex === activeTab
          )}
          onSelectTabIndex={handleTabChange}
          iconLabelArray={tabIconLabelArray}
          classNames="tab-icons-container"
          sx={{
            '& .MuiTab-root': {
              width: `${Math.floor(drawerWidth / tabIconLabelArray.length)}px`,
            },
          }}
        />
        <Divider orientation="horizontal" flexItem />
        <Box className="drawer-scrollable-content">
          {activeTab === ECA_DRAWER_ACTIVE_TAB.LOADS && (
            <div className="loads-tab-area" id="eca-loads-tab">
              <LoadsTab
                activeLoadFile={activeLoadFile}
                onSelectLoadFile={(loadFile, event) =>
                  handleSelectLoadFile(loadFile, event)
                } // Pass event here
                ecaSummaryData={ecaSummaryData}
                loadWithFlyout={loadWithFlyout}
                renderOnPage="doc-search"
              />
              <ECAFlyout
                showFlyout={showFlyout}
                anchorEl={anchorEl}
                onClose={handleClose}
                content={renderLoadFlyoutContent()}
                //  To prevent content to go behind the header on Scroll , need to provide margin , Also need to provide bottom margin, so that flyout is center aligned with the selected element
                containerStyle={{
                  marginTop: headerHeight,
                  marginBottom: headerHeight,
                }}
              />
            </div>
          )}
          {activeTab === ECA_DRAWER_ACTIVE_TAB.SAVE_SEARCHES && (
            <SavedSearchPanel
              selectedIndex={selectedIndex}
              selectedSearch={selectedNode}
              onPickTreeItem={onPickTreeItem}
              onPickSavedSearch={setSelectedSavedSearch}
              expandedIds={expandedIds}
              handleUseAsScope={handleUseAsScope}
              handleViewDocs={handleViewDocs}
              handleAddToTemplate={handleAddToTemplate}
              deleteOption={true}
              isDeleteSuccessful={isDeleteSuccessful}
              ecaAdminPermission={ecaAdmin}
              drawerRef={drawerRef}
              userId={getUserId}
              cacheKey={activeCacheKey}
              cachedRootData={activeCacheEntry?.savedSearchRoot}
              onRootDataChange={updateSavedSearchCache}
            />
          )}
          {activeTab === ECA_DRAWER_ACTIVE_TAB.TAGS && (
            <ECATagTab
              activeTag={selectedTag}
              handleViewDocs={handleViewDocs}
              handleUseAsScope={handleUseAsScope}
              drawerRef={drawerRef}
              cacheKey={activeCacheKey}
              cachedTagData={activeCacheEntry?.tagTree}
              onTagDataChange={updateTagCache}
            />
          )}
        </Box>
      </Box>
    );
  };
  return (
    <>
      {/* Positioned Anchor element , that will have sticky positioning
     flyout remains within the viewport on scroll . */}
      <div
        style={{
          height: 500,
          position: 'absolute',
          top: positions.top - headerHeight,
          left: positions.left - listItemWidth,
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: headerHeight,
          }}
          id="anchorElement"
        ></div>
      </div>
      <div style={{display: 'flex'}}>
        <Drawer
          anchor="left"
          open={showDrawer}
          onClose={handleDrawer}
          variant="persistent"
          PaperProps={{
            ref: drawerRef,
            className: 'drawer-paper',
          }}
          data-testid="eca-drawer"
        >
          {renderDrawerContent()}
        </Drawer>
      </div>

      <div>
        {
          <AurPopup
            className="aur-unsaved-changes-popup no-delete-popup"
            open={noDeleteOption}
            onOk={handleContinue}
            onCancel={closeModalHandler}
            popupHeading={t('eca.flyout.deletePopup.noDeleteHeading')}
            showPopupHeading={true}
            showPopupTitle={true}
            showCloseIcon={true}
            showCancelOption={false}
            popupContent={t('eca.flyout.deletePopup.noDeleteContent')}
            labelActionCancel={t('common.clearAllPopup.buttonCancel')}
            labelActionOk="Close"
            exitButtonHint={t('common.clearAllPopup.cacnelToClearHint')}
            continueButtonHint={t('common.clearAllPopup.confirmToClearHint')}
          />
        }
      </div>

      {showConfirmDeletePopup && (
        <div>
          <AurPopup
            className="aur-unsaved-changes-popup"
            data-testid="eca-unsaved-changes-popup"
            open={showConfirmDeletePopup}
            onOk={handleConfirmDeleteContinue}
            onCancel={closeConfirmDeleteModalHandler}
            popupHeading={t('eca.flyout.deletePopup.heading')}
            popupTitle={t(
              'eca.searchTermReport.termsContent.deletePopup.title'
            )}
            showPopupHeading={true}
            showPopupTitle={true}
            showCloseIcon={false}
            popupContent={t('eca.flyout.deletePopup.confirmMsg')}
            labelActionCancel={t('common.clearAllPopup.buttonCancel')}
            labelActionOk={t('eca.flyout.deletePopup.delete')}
            exitButtonHint={t('common.clearAllPopup.cacnelToClearHint')}
            continueButtonHint={t('common.clearAllPopup.confirmToClearHint')}
          />
        </div>
      )}

      <AurToast
        ariaLabel={t('common.toastAlert.toastAlertHint')}
        hideDuration={5000}
        open={deleteSuccessToast}
        horizontalPosition={'center'}
        message={`${savedSearchLabel} Saved Search was successfully deleted`}
        onClose={() => handleSnackbarClose}
      />
    </>
  );
};

export default ECADrawerWithTabs;
